import type { Locale } from './locale'

// Shared UI strings (site chrome + controls reused across pages). Page-specific
// prose/FAQs live in each page as { en, fr } objects. fr = Canadian French (fr-CA).
type Dict = Record<string, string>

const en: Dict = {
  // nav
  'nav.about': 'About',
  'nav.howToUse': 'How to Use',
  'nav.planner': 'Planner',
  'nav.tools': 'Tools',
  'nav.mySheets': 'My Sheets',
  'nav.resources': 'Resources',
  // a11y
  'a11y.region': 'Shopping region',
  'a11y.theme': 'Toggle dark mode',
  'a11y.menu': 'Menu',
  'a11y.language': 'Language',
  // footer
  'footer.calculators': 'Calculators',
  'footer.guides': 'Guides',
  'footer.company': 'Company',
  'footer.legal': 'Legal',
  'footer.freeKanban': 'Free Kanban Board',
  'footer.contact': 'Contact',
  'footer.howWeCalculate': 'How We Calculate',
  'footer.terms': 'Terms of Use',
  'footer.privacy': 'Privacy Policy',
  'footer.affiliate': 'Affiliate Disclosure',
  'footer.disclaimer': 'All calculations are estimates for planning purposes only. RenoSheets does not provide professional advice.',
  'footer.featuredOn': 'Featured on:',
  'footer.commission': 'Some links may earn us a commission at no extra cost to you.',
  // calculator shared controls
  'calc.measurements': 'Measurements',
  'calc.feet': 'Feet',
  'calc.metres': 'Metres',
  'calc.input': 'Input',
  'calc.dimensions': 'Dimensions',
  'calc.totalArea': 'Total area',
  'calc.areaHint': 'Enter the total area you want to cover.',
  'calc.length': 'Length',
  'calc.width': 'Width',
  'calc.height': 'Height',
  'calc.ceilingHeight': 'Ceiling height',
  'calc.doors': 'Doors',
  'calc.windows': 'Windows',
  'calc.coats': 'Coats',
  'calc.roomType': 'Room Type',
  'calc.roomName': 'Room name',
  'calc.customRoom': 'Custom room',
  'calc.selectRoom': 'Select a room',
  'calc.addRoom': '+ Add another room',
  'calc.addArea': '+ Add another area',
  'calc.remove': 'Remove',
  'calc.wasteAllowance': 'Waste allowance',
  'calc.relatedCalculators': 'Related calculators',
  'calc.soon': '(soon)',
  'calc.faqHeading': 'Frequently asked questions',
  // result sheet
  'result.youllNeed': 'You\'ll need',
  'result.explainAria': 'How we got this number',
  'result.explainTitle': 'How we got this number',
  'result.removeLine': 'Remove from list',
  'result.of': 'of',
  'result.materialsList': 'Materials list',
  'result.toolsYoullWant': 'Tools you\'ll want',
  'result.buyOnce': 'Buy-once items — skip any you already own.',
  'result.save': 'Save to My Sheets',
  'result.saved': '✓ Saved to My Sheets',
  'result.print': 'Print',
  'result.downloadPdf': 'Download PDF',
  'result.share': 'Share',
  'result.linkCopied': '✓ Link copied!',
  'result.whereToBuy': 'Where to buy (optional)',
  'result.whereToBuyNote': 'Happy with your list? Tap any item below to open it at a retailer. These are affiliate links that support the site at no extra cost.',
  'result.estimatesOnly': 'Estimates only — verify quantities before buying.',
  'result.optional': 'optional',
  // affiliate buttons
  'affiliate.shop': 'Shop',
  // breadcrumb
  'crumb.home': 'Home',
  'crumb.resources': 'Resources',
  // guide article shell
  'guide.updated': 'Updated',
  'guide.calculatorsForProject': 'Calculators for this project',
  'guide.getMaterialsList': 'Get your materials list',
  'guide.plannerCtaH2': 'Plan the whole project free',
  'guide.plannerCtaBody': 'Track every step on a free kanban board — no signup, stored privately in your browser.',
  'guide.openPlanner': 'Open the planner',
  'guide.moreGuides': 'More guides',
  'guide.disclaimer': 'This guide is general information for planning, not professional advice. Follow local building codes and product instructions, and consult a licensed pro for structural, electrical, plumbing, or gas work.',
  // planner — columns
  'col.planning': 'Planning',
  'col.planning.desc': 'Researching — not yet bought',
  'col.quoted': 'Quoted',
  'col.quoted.desc': 'Got prices or contractor quotes',
  'col.inprogress': 'In Progress',
  'col.inprogress.desc': 'Materials bought, work underway',
  'col.done': 'Done',
  'col.done.desc': 'Completed',
  // planner — task categories
  'cat.paint': 'Paint', 'cat.flooring': 'Flooring', 'cat.tile': 'Tile', 'cat.concrete': 'Concrete',
  'cat.drywall': 'Drywall', 'cat.electrical': 'Electrical', 'cat.plumbing': 'Plumbing', 'cat.kitchen': 'Kitchen',
  'cat.bathroom': 'Bathroom', 'cat.outdoor': 'Outdoor', 'cat.roofing': 'Roofing', 'cat.structural': 'Structural', 'cat.other': 'Other',
  // planner — board
  'board.board': 'Board',
  'board.buySheet': 'Buy Sheet',
  'board.exportProject': '↓ Export project',
  'board.exportProjectTitle': 'Download this project as a file you can share — recipient opens RenoSheets and uses Import backup to load it.',
  'board.printBoard': '🖨 Print',
  'board.copyLink': '🔗 Copy share link',
  'board.copyLinkTitle': 'Copy a link that auto-imports this project when the recipient opens it.',
  'board.linkCopied': '✓ Link copied!',
  'board.linkTooLong': 'Link is long — for email/chat, prefer the .json file export.',
  'board.addTask': '+ Add task',
  'board.taskName': 'Task name *',
  'board.cost': 'Cost ($)',
  'board.notes': 'Notes (optional)',
  'board.dueDate': 'Due date (optional)',
  'board.importSheet': '▼ Import from a saved sheet',
  'board.hideImport': '▲ Hide',
  'board.select': 'Select…',
  'board.using': 'Using:',
  'board.add': 'Add',
  'board.save': 'Save',
  'board.cancel': 'Cancel',
  'board.edit': 'Edit',
  'board.del': 'Del',
  'board.deleteConfirm': 'Delete this task?',
  'board.moveTo': 'Move to',
  'board.colorAria': 'Colour-code this card',
  'board.dragAria': 'Drag to reorder',
  'board.overdue': 'Overdue',
  'board.dueToday': 'Due today',
  'board.dueIn': 'Due in',
  'color.none': 'None', 'color.red': 'Red', 'color.orange': 'Orange', 'color.amber': 'Amber',
  'color.green': 'Green', 'color.teal': 'Teal', 'color.blue': 'Blue', 'color.purple': 'Purple',
  // planner — stats
  'stats.tasks': 'Tasks',
  'stats.budget': 'Budget',
  'stats.spent': 'Spent (done)',
  // planner — buy sheet
  'buysheet.includeDone': 'Include completed tasks',
  'buysheet.csv': '⬇ CSV',
  'buysheet.print': '🖨 Print',
  'buysheet.alsoNeed': 'You might also need…',
  'buysheet.goesWith': 'goes with',
  'buysheet.empty': 'No tasks yet — add tasks to the board and they\'ll appear here.',
  // planner — my sheets
  'mysheets.title': 'My Sheets — Free Kanban Board & Project Planner | RenoSheets',
  'mysheets.desc': 'Your free kanban board and saved calculations, stored privately in your own browser. No signup — four-column workflow, colour-coded cards, due dates, and backup/restore.',
  'mysheets.h1': 'My Sheets',
  'mysheets.introPre': 'Your free ',
  'mysheets.introLink': 'kanban board',
  'mysheets.introPost': ' and saved calculations — everything is saved in your browser only, nothing is sent to our servers.',
  'mysheets.dataWarning': '⚠ Heads up — everything here lives only in this browser. Clearing your browser data, switching browsers, or using another device will leave it behind. Tap Export backup below to save a copy you can restore anywhere.',
  'mysheets.projects': 'Projects',
  'mysheets.newProject': '+ New project',
  'mysheets.projectName': 'Project name',
  'mysheets.create': 'Create',
  'mysheets.cancel': 'Cancel',
  'mysheets.startFromTemplate': 'Start from a template',
  'mysheets.blankProject': 'Blank project',
  'mysheets.startEmpty': 'Start empty',
  'mysheets.tasksWord': 'tasks',
  'mysheets.noProjects': 'No projects yet. Create one to track your reno tasks across a kanban board.',
  'mysheets.updated': 'Updated',
  'mysheets.delete': 'Delete',
  'mysheets.deleteProject': 'Delete this project and all its tasks?',
  'mysheets.savedSheets': 'Saved Sheets',
  'mysheets.savedSheetsSub': 'Individual calculator results — attach them to a project task.',
  'mysheets.noSheets': 'No saved sheets yet. Run a calculator and tap "Save to My Sheets".',
  'mysheets.remove': 'Remove',
  'mysheets.attach': 'Attach to project →',
  'mysheets.attachCancel': '× Cancel',
  'mysheets.attachPick': 'Pick a project to attach this sheet to:',
  'mysheets.attachNoProjects': 'You don\'t have any projects yet. Create one above, then come back to attach this sheet.',
  'mysheets.editHint': 'Click the sheet to edit name, summary or line quantities.',
  'mysheets.editName': 'Sheet name',
  'mysheets.editSummary': 'Summary',
  'mysheets.editLines': 'Materials list',
  'mysheets.editLabel': 'Item',
  'mysheets.editQty': 'Qty',
  'mysheets.editUnit': 'Unit',
  'mysheets.editSave': 'Save changes',
  'mysheets.editDeleteLine': 'Remove this item',
  'mysheets.editAddLine': '+ Add an item',
  'mysheets.backupH2': 'Backup & restore',
  // Split into three parts so the middle clause can render as <strong> while
  // keeping translations clean (no HTML in i18n strings).
  'mysheets.backupBodyPre': 'Your data is private by design — it lives in this browser only, never on our servers.',
  'mysheets.backupBodyBold': 'Clearing "Cookies and other site data" in your browser will erase all your data!',
  'mysheets.backupBodyPost': 'Export a backup file to keep a copy.',
  'mysheets.exportBackup': '⬇ Export backup',
  'mysheets.importBackup': '⬆ Import backup',
  'mysheets.exportNote': 'The backup JSON file downloads to your browser\'s default Downloads folder. File name starts with "renosheets-backup-".',
  'mysheets.backupMergeNote': 'Importing merges the file into what\'s already here — matching items are updated, the rest are added.',
  'mysheets.importError': 'Could not read that file.',
  // planner — project page
  'projectpage.notFound': 'Project not found.',
  'projectpage.back': '← Back to My Sheets',
  'projectpage.seoDesc': 'Project planner — track your renovation tasks from planning to done.',
  // cookie consent
  'consent.message': 'We use cookies for advertising, analytics, and affiliate links. You can accept or decline non-essential cookies — your calculations and projects always stay in your browser either way.',
  'consent.accept': 'Accept',
  'consent.decline': 'Decline',
  'consent.privacy': 'Privacy Policy',
  'consent.aria': 'Cookie consent',
  'consent.manage': 'Cookie settings',
}

const fr: Dict = {
  // nav
  'nav.about': 'À propos',
  'nav.howToUse': 'Comment l’utiliser',
  'nav.planner': 'Planificateur',
  'nav.tools': 'Outils',
  'nav.mySheets': 'Mes feuilles',
  'nav.resources': 'Ressources',
  // a11y
  'a11y.region': 'Région d’achat',
  'a11y.theme': 'Basculer le mode sombre',
  'a11y.menu': 'Menu',
  'a11y.language': 'Langue',
  // footer
  'footer.calculators': 'Calculateurs',
  'footer.guides': 'Guides',
  'footer.company': 'Entreprise',
  'footer.legal': 'Mentions légales',
  'footer.freeKanban': 'Tableau Kanban gratuit',
  'footer.contact': 'Contact',
  'footer.howWeCalculate': 'Comment nous calculons',
  'footer.terms': 'Conditions d’utilisation',
  'footer.privacy': 'Politique de confidentialité',
  'footer.affiliate': 'Divulgation d’affiliation',
  'footer.disclaimer': 'Tous les calculs sont des estimations à des fins de planification seulement. RenoSheets ne fournit pas de conseils professionnels.',
  'footer.featuredOn': 'Présenté sur :',
  'footer.commission': 'Certains liens peuvent nous rapporter une commission, sans frais supplémentaires pour vous.',
  // calculator shared controls
  'calc.measurements': 'Mesures',
  'calc.feet': 'Pieds',
  'calc.metres': 'Mètres',
  'calc.input': 'Saisie',
  'calc.dimensions': 'Dimensions',
  'calc.totalArea': 'Surface totale',
  'calc.areaHint': 'Entrez la surface totale à couvrir.',
  'calc.length': 'Longueur',
  'calc.width': 'Largeur',
  'calc.height': 'Hauteur',
  'calc.ceilingHeight': 'Hauteur du plafond',
  'calc.doors': 'Portes',
  'calc.windows': 'Fenêtres',
  'calc.coats': 'Couches',
  'calc.roomType': 'Type de pièce',
  'calc.roomName': 'Nom de la pièce',
  'calc.customRoom': 'Pièce personnalisée',
  'calc.selectRoom': 'Choisissez une pièce',
  'calc.addRoom': '+ Ajouter une pièce',
  'calc.addArea': '+ Ajouter une surface',
  'calc.remove': 'Retirer',
  'calc.wasteAllowance': 'Marge de perte',
  'calc.relatedCalculators': 'Calculateurs connexes',
  'calc.soon': '(bientôt)',
  'calc.faqHeading': 'Foire aux questions',
  // result sheet
  'result.youllNeed': 'Il vous faut',
  'result.explainAria': 'Comment nous sommes arrivés à ce chiffre',
  'result.explainTitle': 'Comment nous sommes arrivés à ce chiffre',
  'result.removeLine': 'Retirer de la liste',
  'result.of': 'de',
  'result.materialsList': 'Liste de matériaux',
  'result.toolsYoullWant': 'Outils utiles',
  'result.buyOnce': 'Articles à acheter une seule fois — ignorez ceux que vous avez déjà.',
  'result.save': 'Enregistrer dans Mes feuilles',
  'result.saved': '✓ Enregistré dans Mes feuilles',
  'result.print': 'Imprimer',
  'result.downloadPdf': 'Télécharger le PDF',
  'result.share': 'Partager',
  'result.linkCopied': '✓ Lien copié!',
  'result.whereToBuy': 'Où acheter (facultatif)',
  'result.whereToBuyNote': 'Satisfait de votre liste? Touchez un article ci-dessous pour l’ouvrir chez un détaillant. Ce sont des liens d’affiliation qui soutiennent le site sans frais supplémentaires.',
  'result.estimatesOnly': 'Estimations seulement — vérifiez les quantités avant d’acheter.',
  'result.optional': 'facultatif',
  // affiliate buttons
  'affiliate.shop': 'Acheter chez',
  // breadcrumb
  'crumb.home': 'Accueil',
  'crumb.resources': 'Ressources',
  // guide article shell
  'guide.updated': 'Mis à jour',
  'guide.calculatorsForProject': 'Calculateurs pour ce projet',
  'guide.getMaterialsList': 'Obtenez votre liste de matériaux',
  'guide.plannerCtaH2': 'Planifiez tout le projet gratuitement',
  'guide.plannerCtaBody': 'Suivez chaque étape sur un tableau Kanban gratuit — sans inscription, stocké en privé dans votre navigateur.',
  'guide.openPlanner': 'Ouvrir le planificateur',
  'guide.moreGuides': 'Plus de guides',
  'guide.disclaimer': 'Ce guide est une information générale de planification, et non un avis professionnel. Respectez les codes du bâtiment locaux et les instructions des produits, et consultez un professionnel agréé pour les travaux de structure, d’électricité, de plomberie ou de gaz.',
  // planner — columns
  'col.planning': 'Planification',
  'col.planning.desc': 'Recherche — pas encore acheté',
  'col.quoted': 'Soumission',
  'col.quoted.desc': 'Prix ou soumissions obtenus',
  'col.inprogress': 'En cours',
  'col.inprogress.desc': 'Matériaux achetés, travaux en cours',
  'col.done': 'Terminé',
  'col.done.desc': 'Complété',
  // planner — task categories
  'cat.paint': 'Peinture', 'cat.flooring': 'Plancher', 'cat.tile': 'Carrelage', 'cat.concrete': 'Béton',
  'cat.drywall': 'Gypse', 'cat.electrical': 'Électricité', 'cat.plumbing': 'Plomberie', 'cat.kitchen': 'Cuisine',
  'cat.bathroom': 'Salle de bain', 'cat.outdoor': 'Extérieur', 'cat.roofing': 'Toiture', 'cat.structural': 'Structure', 'cat.other': 'Autre',
  // planner — board
  'board.board': 'Tableau',
  'board.buySheet': 'Liste d’achats',
  'board.exportProject': '↓ Exporter le projet',
  'board.exportProjectTitle': 'Téléchargez ce projet dans un fichier à partager — le destinataire ouvre RenoSheets et utilise Importer la sauvegarde pour le charger.',
  'board.printBoard': '🖨 Imprimer',
  'board.copyLink': '🔗 Copier le lien de partage',
  'board.copyLinkTitle': 'Copiez un lien qui importe automatiquement ce projet quand le destinataire l’ouvre.',
  'board.linkCopied': '✓ Lien copié!',
  'board.linkTooLong': 'Le lien est long — pour courriel/clavardage, préférez l’export en fichier .json.',
  'board.addTask': '+ Ajouter une tâche',
  'board.taskName': 'Nom de la tâche *',
  'board.cost': 'Coût ($)',
  'board.notes': 'Notes (facultatif)',
  'board.dueDate': 'Date d’échéance (facultatif)',
  'board.importSheet': '▼ Importer une feuille enregistrée',
  'board.hideImport': '▲ Masquer',
  'board.select': 'Sélectionner…',
  'board.using': 'Utilise :',
  'board.add': 'Ajouter',
  'board.save': 'Enregistrer',
  'board.cancel': 'Annuler',
  'board.edit': 'Modifier',
  'board.del': 'Suppr',
  'board.deleteConfirm': 'Supprimer cette tâche?',
  'board.moveTo': 'Déplacer vers',
  'board.colorAria': 'Colorer cette carte',
  'board.dragAria': 'Glisser pour réordonner',
  'board.overdue': 'En retard',
  'board.dueToday': 'Échéance aujourd’hui',
  'board.dueIn': 'Dans',
  'color.none': 'Aucune', 'color.red': 'Rouge', 'color.orange': 'Orange', 'color.amber': 'Ambre',
  'color.green': 'Vert', 'color.teal': 'Sarcelle', 'color.blue': 'Bleu', 'color.purple': 'Violet',
  // planner — stats
  'stats.tasks': 'Tâches',
  'stats.budget': 'Budget',
  'stats.spent': 'Dépensé (terminé)',
  // planner — buy sheet
  'buysheet.includeDone': 'Inclure les tâches terminées',
  'buysheet.csv': '⬇ CSV',
  'buysheet.print': '🖨 Imprimer',
  'buysheet.alsoNeed': 'Vous pourriez aussi avoir besoin de…',
  'buysheet.goesWith': 'va avec',
  'buysheet.empty': 'Aucune tâche pour l’instant — ajoutez des tâches au tableau et elles apparaîtront ici.',
  // planner — my sheets
  'mysheets.title': 'Mes feuilles — Tableau Kanban gratuit et planificateur | RenoSheets',
  'mysheets.desc': 'Votre tableau Kanban gratuit et vos calculs enregistrés, stockés en privé dans votre navigateur. Sans inscription — quatre colonnes de flux, cartes en couleur, dates d’échéance et sauvegarde/restauration.',
  'mysheets.h1': 'Mes feuilles',
  'mysheets.introPre': 'Votre ',
  'mysheets.introLink': 'tableau Kanban',
  'mysheets.introPost': ' gratuit et vos calculs enregistrés — tout est sauvegardé dans votre navigateur seulement, rien n’est envoyé à nos serveurs.',
  'mysheets.dataWarning': '⚠ Attention — tout ce qui est ici vit uniquement dans ce navigateur. Effacer les données de navigation, changer de navigateur ou utiliser un autre appareil les laissera derrière. Touchez Exporter la sauvegarde ci-dessous pour conserver une copie restaurable n’importe où.',
  'mysheets.projects': 'Projets',
  'mysheets.newProject': '+ Nouveau projet',
  'mysheets.projectName': 'Nom du projet',
  'mysheets.create': 'Créer',
  'mysheets.cancel': 'Annuler',
  'mysheets.startFromTemplate': 'Partir d’un modèle',
  'mysheets.blankProject': 'Projet vierge',
  'mysheets.startEmpty': 'Partir de zéro',
  'mysheets.tasksWord': 'tâches',
  'mysheets.noProjects': 'Aucun projet pour l’instant. Créez-en un pour suivre vos tâches de réno sur un tableau Kanban.',
  'mysheets.updated': 'Mis à jour',
  'mysheets.delete': 'Supprimer',
  'mysheets.deleteProject': 'Supprimer ce projet et toutes ses tâches?',
  'mysheets.savedSheets': 'Feuilles enregistrées',
  'mysheets.savedSheetsSub': 'Résultats de calculateurs individuels — attachez-les à une tâche de projet.',
  'mysheets.noSheets': 'Aucune feuille enregistrée. Lancez un calculateur et touchez « Enregistrer dans Mes feuilles ».',
  'mysheets.remove': 'Retirer',
  'mysheets.attach': 'Attacher à un projet →',
  'mysheets.attachCancel': '× Annuler',
  'mysheets.attachPick': 'Choisissez un projet où attacher cette feuille :',
  'mysheets.attachNoProjects': 'Vous n’avez aucun projet. Créez-en un ci-dessus, puis revenez attacher cette feuille.',
  'mysheets.editHint': 'Cliquez sur la feuille pour modifier le nom, le résumé ou les quantités.',
  'mysheets.editName': 'Nom de la feuille',
  'mysheets.editSummary': 'Résumé',
  'mysheets.editLines': 'Liste de matériaux',
  'mysheets.editLabel': 'Article',
  'mysheets.editQty': 'Qté',
  'mysheets.editUnit': 'Unité',
  'mysheets.editSave': 'Enregistrer',
  'mysheets.editDeleteLine': 'Retirer cet article',
  'mysheets.editAddLine': '+ Ajouter un article',
  'mysheets.backupH2': 'Sauvegarde et restauration',
  'mysheets.backupBodyPre': 'Vos données sont privées par conception — elles vivent dans ce navigateur seulement, jamais sur nos serveurs.',
  'mysheets.backupBodyBold': 'Effacer « Cookies et autres données de sites » dans votre navigateur effacera toutes vos données!',
  'mysheets.backupBodyPost': 'Exportez un fichier de sauvegarde pour en garder une copie.',
  'mysheets.exportBackup': '⬇ Exporter la sauvegarde',
  'mysheets.importBackup': '⬆ Importer une sauvegarde',
  'mysheets.exportNote': 'Le fichier JSON de sauvegarde est téléchargé dans le dossier Téléchargements par défaut de votre navigateur. Le nom commence par « renosheets-backup- ».',
  'mysheets.backupMergeNote': 'L’importation fusionne le fichier avec ce qui est déjà ici — les éléments correspondants sont mis à jour, les autres sont ajoutés.',
  'mysheets.importError': 'Impossible de lire ce fichier.',
  // planner — project page
  'projectpage.notFound': 'Projet introuvable.',
  'projectpage.back': '← Retour à Mes feuilles',
  'projectpage.seoDesc': 'Planificateur de projet — suivez vos tâches de rénovation de la planification à la fin.',
  // cookie consent
  'consent.message': 'Nous utilisons des témoins (cookies) pour la publicité, l’analyse et les liens d’affiliation. Vous pouvez accepter ou refuser les témoins non essentiels — vos calculs et projets restent de toute façon dans votre navigateur.',
  'consent.accept': 'Accepter',
  'consent.decline': 'Refuser',
  'consent.privacy': 'Politique de confidentialité',
  'consent.aria': 'Consentement aux témoins',
  'consent.manage': 'Gérer les témoins',
}

const TABLE: Record<Locale, Dict> = { en, fr }

export function t(locale: Locale, key: string): string {
  return TABLE[locale][key] ?? en[key] ?? key
}
